import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  PermissionsProvider,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import {appPaths} from '@/config/appPaths';
import {useCountryList} from '@/context/CountryListContext';
import type {AfriexCountry, BusinessCountry} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';

import {
  DetailRow,
  ExpandButton,
  ExpandedPanel,
  NotSet,
  PanelActions,
  PanelColumns,
  PanelSection,
  PillRow,
  StatePill,
} from '../ExpandedRowPanel';
import {
  BUSINESS_COUNTRY_CORRIDOR_FLAGS,
  BUSINESS_SURFACES,
  type BusinessSurface,
  resolveSurfaceFlag,
} from './businessCountryFlags';

type SettingsBusinessCountryTableProps = {
  list: BusinessCountry[];
};

/**
 * A business country is active ON A SURFACE unless that surface's
 * `isDeactivated` is explicitly true, the same sense the server's
 * `findBusinessCountryListByStatus` filters on. Resolved through the flat-key
 * fallback so an un-backfilled country reads here as the server would treat
 * it.
 *
 * Note the Mongoose schema defaults the flat flag to `true`, so a freshly
 * seeded country is inactive until someone turns it on; that is deliberate,
 * and why this reads the stored value rather than assuming a missing flag
 * means active.
 */
const isActiveOn = (item: BusinessCountry, surface: BusinessSurface) =>
  resolveSurfaceFlag(item?.meta?.flags, surface, 'isDeactivated') !== true;

export default function SettingsBusinessCountryTable({
  list,
}: SettingsBusinessCountryTableProps) {
  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-12 px-4">&nbsp;</Th>
          <Th className="py-4 px-2 w-24">Name</Th>
          <Th className="py-4 px-2 w-24">Code</Th>
          <Th className="py-4 px-2 w-24">Business Status</Th>
        </Tr>
      </Thead>

      <TBody className="bg-white divide-y divide-gray-100">
        {list.map((item: BusinessCountry, index: number) => (
          <SettingsBusinessCountryRow
            key={item?.id ?? item?.code ?? index}
            item={item}
            index={index}
          />
        ))}
      </TBody>
    </Table>
  );
}

const SettingsBusinessCountryRow = ({
  item,
  index,
}: {
  item: BusinessCountry;
  index: number;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;

  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  // The record carries its own flag emoji, but only sometimes; fall back to
  // the shared retail country list, which is what every other table here does.
  const flag =
    item?.meta?.codes?.flag ?? mapCountryCodeToFlags(countryList)[item?.code];

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric className="px-4">
          <ExpandButton
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
            label={item?.name}
          />
        </Td>
        <Td className="font-medium">{item?.name}</Td>
        <Td>
          {item?.code} {flag}
        </Td>
        <Td>
          {/*
            Both surfaces, because a country can be live on one and closed on
            the other and a single word would have to hide that.
          */}
          <div className="flex flex-col gap-0.5 text-xs">
            {BUSINESS_SURFACES.map(({key: surface, label}) => (
              <span key={surface}>
                {label}:{' '}
                <span
                  className={
                    isActiveOn(item, surface)
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-red-600 dark:text-red-300'
                  }
                >
                  {isActiveOn(item, surface) ? 'Active' : 'Inactive'}
                </span>
              </span>
            ))}
          </div>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={4} className="p-0 m-0">
            <ExpandedPanel>
              <BusinessCountryDetailPanel data={item} />
            </ExpandedPanel>
          </Td>
        </Tr>
      )}
    </>
  );
};

const BusinessCountryDetailPanel = ({data}: {data: BusinessCountry}) => {
  const {state: countryListState} = useCountryList();
  const flags = data?.meta?.flags;
  const requiredKYB = data?.meta?.kyb?.requiredKYC;

  // Cross-reference against the retail catalogue, so ops can see at a glance
  // whether a corridor open for business is also open for consumers. Read from
  // the shared context list (fetched once on Nav mount) rather than a second
  // request per row.
  const retailCountry = countryListState.countryList.find(
    (country: AfriexCountry) => country?.iso2 === data?.code,
  );

  return (
    <div className="w-full">
      <PanelColumns>
        <div>
          <PanelSection title="Business onboarding">
            <DetailRow
              label="Signup enabled"
              value={flags?.canSignup ? 'Yes' : 'No'}
            />
            <DetailRow
              label="KYB required"
              value={
                requiredKYB?.length
                  ? `Yes (${requiredKYB.length} document${
                      requiredKYB.length === 1 ? '' : 's'
                    })`
                  : 'No'
              }
            />
            <DetailRow
              label="Currency"
              value={data?.meta?.codes?.currencyCode ?? <NotSet />}
            />
          </PanelSection>

          {/*
            One pill row per surface: the dashboard and the merchant API are
            independent, so a single row would have to pick one to show and
            silently hide the other.
          */}
          {BUSINESS_SURFACES.map(({key: surface, label}) => (
            <PanelSection
              key={surface}
              title={`Corridor permissions (${label})`}
              className="mt-5"
            >
              <PillRow>
                {BUSINESS_COUNTRY_CORRIDOR_FLAGS.filter(
                  ({key}) => key !== 'isDeactivated',
                ).map(({key, label: flagLabel}) => (
                  <StatePill
                    key={key}
                    label={flagLabel}
                    allowed={resolveSurfaceFlag(flags, surface, key)}
                  />
                ))}
                <StatePill
                  label="Active"
                  allowed={
                    resolveSurfaceFlag(flags, surface, 'isDeactivated') !== true
                  }
                />
              </PillRow>
            </PanelSection>
          ))}
        </div>

        <PanelSection title="Also active for B2C">
          <p className="text-sm text-gray-700 dark:text-gray-100">
            {retailCountry
              ? `Yes, listed under Countries as ${retailCountry.status}`
              : 'No, this country has no retail (B2C) record'}
          </p>
        </PanelSection>
      </PanelColumns>

      <PermissionsProvider
        permission={AfriexAdminPermission.BusinessCountryUpdate}
      >
        <PanelActions>
          <Button
            variant="solid"
            onClick={() =>
              window.open(
                `../${appPaths.config}/business-country/${data?.code}`,
                '_self',
              )
            }
          >
            Manage business country
          </Button>
        </PanelActions>
      </PermissionsProvider>
    </div>
  );
};
